import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

/**
 * The props for the `AuthLayout` component.
 * Configures the centered authentication page wrapper.
 */
interface ComponentProps {
    /** The page content rendered inside the centered wrapper. */
    children: ReactNode;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'AuthLayout'
     */
    testId?: string;
}

/**
 * A centered layout wrapper for authentication pages (login, register, account).
 * Provides a max-width container with vertical padding, used consistently
 * across all auth-related pages. The `AppHeader` above it is rendered persistently
 * by `_app.tsx`, outside the page-transition animation.
 *
 * @param props          The component props.
 * @param props.children The page content.
 * @param props.testId   An optional test ID used for automation selectors. Defaults to `'AuthLayout'`.
 * @returns A JSX element rendering the AuthLayout.
 *
 * @example
 * ```tsx
 * <AuthLayout>
 *     <LoginForm />
 * </AuthLayout>
 * ```
 */
const AuthLayout = ({children, testId = 'AuthLayout'}: ComponentProps) => (
    <Page data-cy={testId}>
        <Wrapper>{children}</Wrapper>
    </Page>
);

AuthLayout.displayName = 'AuthLayout';

export {AuthLayout};

// ── Styled ──────────────────────────────────────────────────────────────

const Page = styled.div`
    background: ${({theme}) => theme.colors.surfaceBackground};
    min-height: 100dvh;
`;

const Wrapper = styled.main`
    margin: 0 auto;
    max-width: 420px;
    padding: ${spacing(16)} ${spacing(6)};
`;